// frontend/src/lib/coaImport.ts
import type { CoaAccount as BaseCoaAccount, CoaAccountType } from '../components/CoaPreview';
import type { CoaImportProposal } from '../components/CoaMapReview';

export const MAX_IMPORT_ROWS = 500;
const MAX_BALANCE = 999_999_999_999;
const PLACEHOLDER_TYPE: Record<string, CoaAccountType> = {
  '1': 'asset', '2': 'liability', '3': 'equity', '4': 'revenue',
  '5': 'cost', '6': 'expense', '8': 'other_income', '9': 'other_expense',
};

export interface CoaAccount extends BaseCoaAccount {
  opening_balance?: number;
}

export interface RawCoaRow { rowIndex: number; code: string; name: string; classification?: string; type?: string; balance?: number; }

/** Account numbers are stored as 5-character strings so leading zeroes survive. */
export const COA_ACCOUNT_NUMBER_PATTERN = /^[0-9]{5}$/;

/** Validation errors are collected first so the user can fix all rows at once. */
export class CoaImportValidationError extends Error {
  readonly issues: string[];
  constructor(issues: string[]) {
    super(issues.join('\n'));
    this.name = 'CoaImportValidationError';
    this.issues = issues;
  }
}

/** Error-backed so array.join() surfaces messages (viewer code uses row/message fields). */
export class ImportIssue extends Error {
  row: number | null;
  constructor(row: number | null, message: string) {
    super(message);
    this.row = row;
    this.name = 'ImportIssue';
  }
  toString(): string { return this.message; }
}

export interface CoaImportResult { accounts: CoaAccount[]; errors: ImportIssue[]; warnings: ImportIssue[]; }

const issue = (row: number | null, message: string): ImportIssue => new ImportIssue(row, message);

export function cleanCode(raw: unknown): string {
  const digits = String(raw ?? '').replace(/[^0-9]/g, '');
  return digits === '' ? '' : String(parseInt(digits, 10));
}

/**
 * Parse an opening balance from any tabular source. Numeric cells keep their
 * number; text is normalized independently of Excel so a future CSV/text
 * importer can reuse this function.
 *
 * Accepted accounting notation (debit is positive, credit is negative):
 *   `1234.56`, `1,234.56`, `1，234.56`, `-1,234.56`,
 *   `(1,234.56)`, `（1,234.56）`, and trailing-minus `1,234.56-`.
 * Blank/null returns NaN; callers decide whether blank is allowed.
 */
export function parseBalanceCell(raw: unknown): number {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : NaN;
  let s = String(raw ?? '')
    .trim()
    .replace(/，/g, ',')
    .replace(/（/g, '(')
    .replace(/）/g, ')')
    .replace(/－/g, '-');
  if (!s) return NaN;
  s = s.replace(/,/g, '');
  let negative = false;
  if (s.length >= 2 && s.startsWith('(') && s.endsWith(')')) {
    negative = true;
    s = s.slice(1, -1).trim();
  } else if (s.endsWith('-')) {
    negative = true;
    s = s.slice(0, -1).trim();
  }
  const n = Number(s);
  return Number.isFinite(n) ? (negative ? -n : n) : NaN;
}

/** Source-neutral row shape so XLSX and future CSV/text readers share one validator. */
export interface CoaRowInput {
  rowIndex: number;
  accountNumber: unknown;
  accountName: unknown;
  balance?: unknown;
}

export interface CoaRowValidationResult {
  rowIndex: number;
  code: string;
  name: string;
  balance?: number;
  issues: string[];
}

function hasValue(value: unknown): boolean {
  return value !== '' && value !== undefined && value !== null;
}

/** Validate one row without any Excel-specific type coercion. */
export function validateCoaRow(row: CoaRowInput): CoaRowValidationResult {
  const issues: string[] = [];
  const code = typeof row.accountNumber === 'number'
    ? String(row.accountNumber)
    : String(row.accountNumber ?? '').trim();
  const name = String(row.accountName ?? '').trim();

  if (!COA_ACCOUNT_NUMBER_PATTERN.test(code)) {
    issues.push('Account Number must be exactly 5 digits');
  }
  if (!name) issues.push('Account Name is required');

  const result: CoaRowValidationResult = { rowIndex: row.rowIndex, code, name, issues };
  if (hasValue(row.balance)) {
    const balance = parseBalanceCell(row.balance);
    if (Number.isNaN(balance)) {
      issues.push('Balance b/f must be numeric');
    } else {
      result.balance = balance;
    }
  }
  return result;
}

export async function readCoaFile(file: File): Promise<RawCoaRow[]> {
  const XLSX = await import('xlsx');
  const ext = file.name.toLowerCase().split('.').pop() || '';
  if (ext !== 'xlsx' && ext !== 'xls') {
    throw new Error('Only Excel (.xlsx/.xls) files are supported');
  }
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) return [];
  const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: true, blankrows: false, defval: '' });

  // Header-aware column mapping. The generated template carries the 5-column
  // MYOB-aligned layout (Account Number | Account Name | Classification | Type |
  // Balance b/f) while legacy files are positional (Code | Name | Balance).
  // Classification/Type are optional hints for /firms/coa-map; unknown text is
  // harmless because the mapper falls back to the code's class digit.
  const headerRow = (grid[0] ?? []).map(c => String(c ?? '').trim().toLowerCase());
  const hasHeader = headerRow.some(h => /code|name|balance|classif|type|編碼|名稱|結餘|分类|分類|類型|类型/.test(h));
  let codeIdx = 0, nameIdx = 1, classIdx = -1, typeIdx = -1, balIdx = -1;
  if (hasHeader) {
    headerRow.forEach((h, i) => {
      if (/classif|分類|分类/.test(h)) classIdx = i;
      else if (/type|類型|类型/.test(h)) typeIdx = i;
      else if (/balance|結餘|结余/.test(h)) balIdx = i;
      else if (/number|code|編碼|编码|帳號|账号/.test(h)) codeIdx = i;
      else if (/name|名稱|名称/.test(h)) nameIdx = i;
    });
  }
  if (balIdx < 0) balIdx = 2; // legacy positional layout

  const validationIssues: string[] = [];
  const rows: RawCoaRow[] = [];
  grid.forEach((r: unknown[], idx) => {
    if (hasHeader && idx === 0) return; // recognized header
    const rowNumber = idx + 1;
    if (r.every(cell => cell === '' || cell === undefined || cell === null)) return;
    const rawCode = r[codeIdx];
    const balanceCell = r[balIdx];
    const validated = validateCoaRow({
      rowIndex: rowNumber,
      accountNumber: rawCode,
      accountName: r[nameIdx],
      ...(hasValue(balanceCell) ? { balance: balanceCell } : {}),
    });

    const classification = classIdx >= 0 ? String(r[classIdx] ?? '').trim() : '';
    const type = typeIdx >= 0 ? String(r[typeIdx] ?? '').trim() : '';
    if (validated.issues.length > 0) {
      validationIssues.push(`Row ${rowNumber}: ${validated.issues.join('; ')}`);
      return;
    }

    rows.push({
      rowIndex: validated.rowIndex,
      code: validated.code,
      name: validated.name,
      ...(classification ? { classification } : {}),
      ...(type ? { type } : {}),
      ...(validated.balance !== undefined ? { balance: validated.balance } : {}),
    });
  });
  if (validationIssues.length > 0) {
    throw new CoaImportValidationError(validationIssues);
  }
  return rows;
}

/**
 * `readCoaFile` is positional/header-aware and can misread messy sheets. When it
 * yields no row carrying both a code and a name, the caller falls back to sending
 * the first sheet as raw CSV text for the LLM to parse (D8).
 */
export function looksUsable(rows: RawCoaRow[]): boolean {
  return rows.length >= 1 && rows.some(r => String(r.code ?? '').trim() !== '' && String(r.name ?? '').trim() !== '');
}

/** Convert the first worksheet of an Excel file to CSV text (hybrid raw-text path). */
export async function sheetToCsv(file: File): Promise<string> {
  const XLSX = await import('xlsx');
  const ext = file.name.toLowerCase().split('.').pop() || '';
  if (ext !== 'xlsx' && ext !== 'xls') {
    throw new Error('Only Excel (.xlsx/.xls) files are supported');
  }
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) return '';
  return XLSX.utils.sheet_to_csv(sheet);
}

const CODE_TYPE_BY_DIGIT: Record<string, CoaAccountType> = {
  '1': 'asset', '2': 'liability', '3': 'equity', '4': 'revenue',
  '5': 'cost', '6': 'expense', '8': 'other_income', '9': 'other_expense',
};

/**
 * Parse only the expected upload template. Unlike `readCoaImportSource`, this
 * never converts a rejected sheet into raw text for AI cleanup: invalid rows
 * fail immediately so the demo import cannot invent or infer source data.
 */
export async function readStrictCoaImportRows(file: File): Promise<RawCoaRow[]> {
  const rows = await readCoaFile(file);
  if (!looksUsable(rows)) {
    throw new CoaImportValidationError(['No usable Account Number and Account Name columns were found.']);
  }
  const invalidClass = rows.filter(row => !CODE_TYPE_BY_DIGIT[row.code[0]]);
  if (invalidClass.length > 0) {
    throw new CoaImportValidationError(invalidClass.map(row => `Row ${row.rowIndex}: unsupported account class ${row.code[0]}`));
  }
  return rows;
}

/** Build review rows directly from the strict template rows. */
export function buildStrictCoaImportProposals(rows: RawCoaRow[]): CoaImportProposal[] {
  const codes = new Set(rows.map(row => row.code));
  const balancedCodes = new Set(rows.filter(row => (row.balance ?? 0) !== 0).map(row => row.code));
  return rows.map((row): CoaImportProposal => {
    const parent = roundDownParent(row.code);
    const usableParent = parent && codes.has(parent) && !balancedCodes.has(parent) ? parent : null;
    return {
      imported_code: row.code,
      imported_name: row.name,
      account_code: row.code,
      account_name: row.name,
      account_type: CODE_TYPE_BY_DIGIT[row.code[0]],
      // Only preserve an uploaded hierarchy when its exact ancestor was uploaded;
      // otherwise a parentless leaf keeps its uploaded opening balance safely.
      parent_code: usableParent,
      ...(row.balance !== undefined ? { balance: row.balance } : {}),
      reason: 'Strict import: code, name and opening balance were taken directly from the uploaded template.',
      source: 'heuristic',
    };
  });
}

/** Hybrid import source: structured rows when the strict reader accepts the
 *  sheet, otherwise the first sheet as CSV text for the LLM to parse. */
export interface CoaImportSource { rows?: RawCoaRow[]; rawText?: string }

/**
 * Decide the hybrid source for a file (D8). `readCoaFile` enforces exactly
 * 5-digit account numbers / numeric balances and THROWS `CoaImportValidationError`
 * on a sheet it cannot accept; that (or a usable-row miss) routes to the raw-text
 * path so the LLM can clean the file. Returns `{}` when neither path yields data.
 */
export async function readCoaImportSource(file: File): Promise<CoaImportSource> {
  try {
    const rows = await readCoaFile(file);
    if (looksUsable(rows)) return { rows };
  } catch { /* strict parser rejected the sheet — fall back to raw text */ }
  const rawText = (await sheetToCsv(file)).trim();
  return rawText ? { rawText } : {};
}

function pad5(code: string): string { return code + '0'.repeat(5 - code.length); }

function roundDownParent(code5: string): string | null {
  if (code5.endsWith('0000')) return null;
  if (code5.endsWith('000')) return code5.slice(0, 1) + '0000';
  if (code5.endsWith('00')) return code5.slice(0, 2) + '000';
  return code5.slice(0, 3) + '00';
}

/** Naive stem parent for mismatch detection (warnings only, never used as result). */
function stemParent(code5: string): string | null {
  const stem = code5.replace(/0+$/, '') || code5.slice(0, 1);
  if (stem.length <= 1) return null;
  return pad5(stem.slice(0, -1));
}

export function buildImport(rows: RawCoaRow[]): CoaImportResult {
  const errors: ImportIssue[] = [];
  const warnings: ImportIssue[] = [];
  let accounts = new Map<string, CoaAccount>();

  if (rows.length > MAX_IMPORT_ROWS) {
    errors.push(issue(null, `File exceeds ${MAX_IMPORT_ROWS} accounts`));
  }
  const names = new Set<string>();
  const codes = new Set<string>();
  for (const r of rows) {
    const { rowIndex, name } = r;
    const rawDigits = r.code;
    let code = r.code;
    if (!COA_ACCOUNT_NUMBER_PATTERN.test(code)) {
      errors.push(issue(rowIndex, `Invalid code "${r.code}" (must be exactly 5 digits)`));
      continue;
    }
    if (!name.trim()) { errors.push(issue(rowIndex, `Row ${rowIndex}: missing account name`)); continue; }
    let type = PLACEHOLDER_TYPE[rawDigits[0] ?? code[0]];
    if (!type) { errors.push(issue(rowIndex, `Invalid class digit in code "${r.code}"`)); continue; }
    // MYOB class 8 is predominantly other income, but expense ledgers such as
    // “Income tax Expenses” also use an 8xxxx code. Route those debit-natural
    // rows to the Other Expense class before duplicate/hierarchy processing.
    if (type === 'other_income' && /\bexpenses?\b/i.test(name)) {
      type = 'other_expense';
      code = `9${code.slice(1)}`;
      warnings.push(issue(rowIndex, `Class-8 expense remapped to ${code} (Other Expense)`));
    }
    if (codes.has(code)) { errors.push(issue(rowIndex, `Duplicate code ${code}`)); continue; }
    if (names.has(name.trim().toLowerCase())) { errors.push(issue(rowIndex, `Duplicate account name "${name}"`)); continue; }
    if (r.balance !== undefined && (!Number.isFinite(r.balance) || Math.abs(r.balance) > MAX_BALANCE)) {
      errors.push(issue(rowIndex, `Invalid Balance b/f "${r.balance}"`));
      continue;
    }
    codes.add(code); names.add(name.trim().toLowerCase());
    // CSV exports commonly use negative signs for credit-natural balances.
    // Store balances normal-side, so a negative liability/equity/income value
    // becomes positive. Debit-natural rows keep their sign for contra balances.
    const isDebitNatural = type === 'asset' || type === 'cost' || type === 'expense' || type === 'other_expense';
    const openingBalance = r.balance === undefined || !Number.isFinite(r.balance)
      ? r.balance
      : (isDebitNatural ? r.balance : Math.abs(r.balance));
    accounts.set(code, {
      account_code: code, account_name: name.trim(), account_type: type, parent_code: null,
      ...(r.balance !== undefined ? { opening_balance: openingBalance } : {}), is_custom: true,
    });
  }
  if (errors.length > 0) return { accounts: [], errors, warnings };

  const allCodes = [...accounts.keys()];
  const mixed = new Set(allCodes.map(c => c.length)).size > 1;

  // Rebuild into a NEW map over a snapshot (never mutate a Map while iterating it).
  if (mixed) {
    const next = new Map<string, CoaAccount>();
    for (const a of [...accounts.values()]) {
      const prefix = allCodes
        .filter(p => p.length < a.account_code.length && a.account_code.startsWith(p))
        .sort((x, y) => y.length - x.length)[0];
      const padded = pad5(a.account_code);
      if (padded !== a.account_code) {
        warnings.push(issue(null, `Code ${a.account_code} padded to ${padded}`));
      }
      // never self-parent: if the prefix's padded form IS this code, fall back to round-down
      let parent: string | null;
      if (prefix) {
        const prefixPadded = pad5(prefix);
        if (prefixPadded === padded) {
          parent = roundDownParent(padded);
          warnings.push(issue(null, `Code ${padded} staged under itself; parent fell back to ${parent ?? 'root'}`));
        } else {
          parent = prefixPadded;
        }
      } else {
        parent = roundDownParent(padded);
      }
      next.set(padded, { ...a, account_code: padded, parent_code: parent });
    }
    if (next.size < accounts.size) {
      errors.push(issue(null, `Duplicate code after padding: two accounts collapse to the same 5-digit code (padding collision at codes ${[...accounts.keys()].join(', ')})`));
      return { accounts: [], errors, warnings };
    }
    accounts = next;
  } else {
    const next = new Map<string, CoaAccount>();
    for (const a of [...accounts.values()]) {
      const c = pad5(a.account_code);
      if (c !== a.account_code) warnings.push(issue(null, `Code ${a.account_code} padded to ${c}`));
      next.set(c, { ...a, account_code: c, parent_code: roundDownParent(c) });
    }
    accounts = next;
  }

  // parent-reference links to codes that exist; auto-create missing ancestors recursively
  const created: string[] = [];
  for (const a of [...accounts.values()]) {
    let parent: string | null = a.parent_code;
    while (parent && !accounts.has(parent)) {
      const header: CoaAccount = {
        account_code: parent, account_name: `自動 ${parent} Group`,
        account_type: PLACEHOLDER_TYPE[parent[0]], parent_code: roundDownParent(parent), is_custom: true,
      };
      accounts.set(parent, header); created.push(parent);
      parent = header.parent_code;
    }
  }
  if (created.length > 0) {
    warnings.push(issue(null, `Auto-created parent account(s): ${created.join(', ')}`));
  }

  // Some exports put an opening balance on a code that is also inferred as a
  // parent. Keep that balance usable by promoting its children to the next
  // ancestor; otherwise parent filtering silently drops the balance from the
  // trial-balance totals.
  for (const account of [...accounts.values()]) {
    if (!account.opening_balance) continue;
    const children = [...accounts.values()].filter(child => child.parent_code === account.account_code);
    for (const child of children) {
      child.parent_code = account.parent_code;
    }
  }

  if (accounts.size > MAX_IMPORT_ROWS) {
    errors.push(issue(null, `Import exceeds ${MAX_IMPORT_ROWS} accounts after adding parents`));
    return { accounts: [], errors, warnings };
  }

  // assumption warnings: naive stem parent differing from computed parent (rollover / 5th-level flatten)
  for (const a of [...accounts.values()]) {
    const naive = stemParent(a.account_code);
    if (naive && naive !== a.parent_code) {
      warnings.push(issue(null, `${a.account_code} assumed to be a leaf under ${a.parent_code ?? 'root'}`));
    }
  }

  const sorted = [...accounts.values()].sort((x, y) => x.account_code.localeCompare(y.account_code));
  // imported rows (with their balances) come first; auto-created placeholder headers follow
  const createdSet = new Set(created);
  const list = [...sorted.filter(a => !createdSet.has(a.account_code)), ...sorted.filter(a => createdSet.has(a.account_code))];
  // balances travel with the account object (opening_balance is spread through every remap)
  return { accounts: list.map(a => ({ ...a, is_custom: true })), errors, warnings };
}

export interface CoaTemplateRow {
  account_code: string;
  account_name: string;
  account_type?: CoaAccountType;
  parent_code?: string | null;
}

/** MYOB-style classification labels emitted in the template's Classification
 *  column. Chosen so the deterministic formatter's resolveTypeFromHint
 *  round-trips every label back to its CoaAccountType ('Other Income' before
 *  'Income' matters). */
export const CLASSIFICATION_LABELS: Record<CoaAccountType, string> = {
  asset: 'Asset',
  liability: 'Liability',
  equity: 'Equity',
  revenue: 'Income',
  cost: 'Cost of Sales',
  expense: 'Expense',
  other_income: 'Other Income',
  other_expense: 'Other Expense',
};

/** Build the downloadable Excel (.xlsx) template. When template accounts are
 *  supplied, one row is emitted per account (names verbatim, classification
 *  prefilled, opening balance left blank); otherwise falls back to the built-in
 *  sample COA rows. The finer MYOB Type is deliberately NOT emitted — the LLM
 *  mapper infers it from the name/classification when a row is imported. */
export async function coaTemplateXlsx(accounts?: CoaTemplateRow[]): Promise<Blob> {
  const XLSX = await import('xlsx');
  const header = ['Account Number', 'Account Name', 'Classification', 'Balance b/f'];
  const rows = accounts && accounts.length > 0
    ? accounts.map(a => [
        a.account_code,
        a.account_name,
        a.account_type ? CLASSIFICATION_LABELS[a.account_type] ?? '' : '',
        '',
      ])
    : [
        ['11101', '銀行存款 HSBC', 'Asset', 50000],
        ['21101', '應付賬款 Trade Creditors', 'Liability', 12000],
      ];
  const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'COA');
  const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
  return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
